// Registers bootstrap-vue-next globally and translates the legacy bootstrap-vue (Vue 2)
// props/events still used in templates, so screens keep working without per-file edits.
import { h, defineComponent } from 'vue';
import * as BVN from 'bootstrap-vue-next';

const isSet = (v) => v !== undefined && v !== false;
const kebab = (s) => s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

// Reads a prop written as either kebab-case or camelCase, removing it from attrs.
const take = (attrs, name) => {
  for (const key of [kebab(name), name]) {
    if (key in attrs) {
      const value = attrs[key];
      delete attrs[key];
      return value === '' ? true : value;
    }
  }
  return undefined;
};

// Merges a listener into attrs without dropping an existing one (e.g. from v-model).
const addListener = (attrs, event, fn) => {
  const prev = attrs[event];
  attrs[event] = prev ? [].concat(prev, fn) : fn;
};

const moveListener = (attrs, from, to, transform = (...args) => args) => {
  const handler = attrs[from];
  if (!handler) return;
  delete attrs[from];
  const handlers = [].concat(handler);
  addListener(attrs, to, (...args) => handlers.forEach((fn) => fn(...transform(...args))));
};

const adapt = (Base, mapAttrs) =>
  defineComponent({
    name: `Compat${Base.name || ''}`,
    inheritAttrs: false,
    setup(_, { attrs, slots }) {
      return () => {
        const next = { ...attrs };
        mapAttrs(next);
        return h(Base, next, slots);
      };
    },
  });

// Bootstrap-vue emitted the value on input/change; bootstrap-vue-next only emits update:modelValue.
const valueEvents = (attrs, valueProp = 'value') => {
  const value = take(attrs, valueProp);
  if (value !== undefined && !('modelValue' in attrs)) attrs.modelValue = value;
  moveListener(attrs, 'onInput', 'onUpdate:modelValue');
  moveListener(attrs, 'onChange', 'onUpdate:modelValue');
};

const visibilityToModel = (attrs, prop) => {
  const value = take(attrs, prop);
  if (value !== undefined && !('modelValue' in attrs)) attrs.modelValue = value;
  moveListener(attrs, 'onChange', 'onUpdate:modelValue');
};

const overrides = {
  BModal: adapt(BVN.BModal, (a) => {
    if (isSet(take(a, 'hideFooter'))) a.noFooter = true;
    if (isSet(take(a, 'hideHeader'))) a.noHeader = true;
    visibilityToModel(a, 'visible');
  }),
  BTable: adapt(BVN.BTable, (a) => {
    // Every table scrolls horizontally on small screens instead of overflowing the page.
    if (!('responsive' in a)) a.responsive = true;
    moveListener(a, 'onRowSelected', 'onRowSelected', (item) => [item == null ? [] : [item]]);
    moveListener(a, 'onRowClicked', 'onRowClicked', (o) => [o.item, o.index, o.event]);
    moveListener(a, 'onSortChanged', 'onSorted', (s) => [
      { sortBy: s && s.key, sortDesc: !!s && s.order === 'desc' },
    ]);
  }),
  BAlert: adapt(BVN.BAlert, (a) => visibilityToModel(a, 'show')),
  BCollapse: adapt(BVN.BCollapse, (a) => visibilityToModel(a, 'visible')),
  BButton: adapt(BVN.BButton, (a) => {
    if (isSet(take(a, 'block'))) a.class = [a.class, 'w-100'];
  }),
  BDropdown: adapt(BVN.BDropdown, (a) => {
    if (isSet(take(a, 'right'))) a.end = true;
  }),
  BFormCheckbox: adapt(BVN.BFormCheckbox, (a) => valueEvents(a, 'checked')),
  BFormInput: adapt(BVN.BFormInput, (a) => valueEvents(a)),
  BFormTextarea: adapt(BVN.BFormTextarea, (a) => valueEvents(a)),
  BFormSelect: adapt(BVN.BFormSelect, (a) => valueEvents(a)),
  BPagination: adapt(BVN.BPagination, (a) => {
    if (isSet(take(a, 'hideGotoEndButtons'))) a.noGotoEndButtons = true;
    // bootstrap-vue always showed page 1, even with no rows.
    const total = take(a, 'totalRows');
    a.totalRows = Math.max(Number(total) || 0, 1);
    valueEvents(a);
  }),
  // Removed in Bootstrap 5: keep the Bootstrap 4 wrapper so existing CSS still applies.
  BInputGroupAppend: defineComponent({
    name: 'BInputGroupAppend',
    setup: (_, { slots }) => () => h('div', { class: 'input-group-append' }, slots.default && slots.default()),
  }),
};

// vue-select 4 uses modelValue; templates still pass :value + @input.
export const compatVSelect = (VSelect) => adapt(VSelect, (a) => valueEvents(a));

// vue-datepicker-next binds `value` / update:value instead of modelValue.
export const compatDatePicker = (DatePicker) =>
  adapt(DatePicker, (a) => {
    if ('modelValue' in a) {
      a.value = a.modelValue;
      delete a.modelValue;
    }
    moveListener(a, 'onUpdate:modelValue', 'onUpdate:value');
  });

export default {
  install(app) {
    for (const name of BVN.componentNames) {
      const component = overrides[name] || BVN[name];
      if (!component) continue;
      app.component(name, component);
    }
    app.component('BInputGroupAppend', overrides.BInputGroupAppend);
    app.directive('b-tooltip', BVN.vBTooltip);
    app.directive('b-toggle', BVN.vBToggle);
    app.directive('b-modal', BVN.vBModal);
  },
};
